import { BehaviorSubject } from "rxjs";
import { UIAppConfig } from '../models/uiapp-config';

// chart color array
export const ALARM_CHART_COLORS = ['#dc3848', '#ffc105', '#29a847'];
export const ALARM_CHART_ICONS = ['tank-critical-low-alarm.svg', 'tank-low-alarm.svg', 'tank-safety-stock-alarm.svg'];
//export const ALARM_CHART_COLORS = ['#e56590', '#b3d29e', '#f7d7bc'];
//yellow | warning = 	#ffc105
//red | failure  = 	#dc3848
//green | success = 	#29a847

//export const CHART_COLORS = ['#BFD5EB', '#008ADD', '#3DA3E5', '#88DEB3', '#C08667', '#7B9586', '#C074C0', '#A596E9', '#AAAAAA', '#FFFFFF'];
export const CHART_COLORS = ['#8CD9B3', '#00AB4E', '#788C50', '#21775D', '#0088DD', '#2D3A86', '#7644C1', '#871C18', '#DF292B', '#FF8889', '#E3722C', '#FDB933'];

// export const BAR_CHART_COLORS = ['#DF5555', '#DDDD55', '#33DD33'];
export const BAR_CHART_ICONS = ['tank-critical-low-alarm.svg', 'tank-low-alarm.svg', 'tank-safety-stock-alarm.svg'];
export const BAR_CHART_COLORS =  ['#dc3848', '#ffc105', '#29a847'];

export const BAR_CHART_STROKE_COLORS = ['#DEC27A', '#7B93A1', '#779586'];
/**
 * What a session does when it learns its selected organization was changed somewhere else - another
 * tab, the mobile app, or the legacy app writing the shared column.
 *
 * - `silent`   do nothing at all. Tabs are simply independent, each scoped to the organization it
 *             was opened in, and nobody is told anything.
 * - `notify`   as `silent`, plus a message so the user knows why another tab disagrees with this one.
 * - `converge` follow the change: fetch a credential carrying the new organization, re-read the
 *             profile and bust the caches, then tell the user their page is stale.
 *
 * All three are coherent because the CSO claim on a session's own token is the ONLY thing any
 * scoping or authorization decision reads. While services still read User.LastSelectedOrganizationId
 * directly, anything but `converge` was unsafe - a tab on an older claim got its scope from
 * whichever of the two a given backend service happened to consult.
 *
 * KNOWN LIMIT, and it applies to `silent` and `notify` alike: a tab's organization lives in its
 * in-memory token, which a page reload destroys. The APP_INITIALIZER then trades the refresh cookie
 * for a new one through Authenticate/refresh, which rebuilds the claims from
 * User.LastSelectedOrganizationId - a column shared by every session. So per-tab context survives
 * navigation inside the SPA but NOT F5, where the tab adopts whatever organization was selected
 * last, anywhere. Making it survive would mean the tab naming its own organization on refresh
 * (sessionStorage is per-tab by definition), validated server-side like any other selection.
 */
export type CsoChangeBehaviour = 'silent' | 'notify' | 'converge';

export class AppSettings {
  public static isLoadingCounter = [];
  public static buildInfo: string = null;
  public static showBuildInfo = false;
  public static showBuildInfo$ = new BehaviorSubject<boolean>(false);
  public static heartBeatInterval = 5; //Minutes
  public static csoChangeBehaviour: CsoChangeBehaviour = 'silent';
  public static debounceTime = 500; //1/2 second
  public static minResponseDelay = 3; //Seconds
  public static maxCacheAgeShorter = 10 * 1000; //Seconds
  public static maxCacheAgeShort = 5 * 60 * 1000; // 5 minutes
  public static maxCacheAgeLong = 60 * 60 * 1000; // 1 hour
  public static maxCacheAgeLonger = 24 * 60 * 60 * 1000; // 24 hours
  public static TankLinkToTap = {
    IsEnabled: true,
    pollingInterval: 5000,
    TvpToTp: true
  };

  public static UIAppConfig: UIAppConfig = null;
  public static chartThemeLight: 'ag-material';
  public static chartThemeDark: 'ag-default-dark';
  public static tapStateExisted: boolean = false;
}

export const minDateTime: string = '1900-01-01T12:00:00';

export const mapAlarmColors = ['#00AB4E', '#FDB933', '#DF292B', '#808080'];
export const mapMarkerIcons = ['pin-green.svg', 'pin-yellow.svg', 'pin-red.svg', 'pin-inactive.svg'];


//Google Map Styles
export const tapMapStyles: any = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5',
      },
    ],
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161',
      },
    ],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#f5f5f5',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#bdbdbd',
      },
    ],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#6c6c6c',
      },
      {
        saturation: 50,
      },
      {
        lightness: -100,
      },
    ],
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161',
      },
    ],
  },
  {
    featureType: 'road.local',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
  {
    featureType: 'transit',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5',
      },
    ],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c9c9c9',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
];

export enum FileSize {
  MB_5 = 5242880,
  MB_10 = 10485760,
  MB_15 = 15728640,
  MB_20 = 20971520,
}

export enum FileStatusCode {
  Invalid = '0',
  Valid = '1',
  Uploaded = '2',
}

export enum TAPErrorCodes {
  OrgNotInScope = 1001,
  LocNotInScope = 1002,
  UserNotInScope = 1003,
  TankNotInScope = 1004,
  ReportNotAvailable = 1005,
}

export const PRE_TELEMETRY_FALLBACK_TEXT = 'Data currently available only for pre-telemetry tanks.';
export const PRE_TELEMETRY_RESOURCE_KEY = 'TankGrid.PreTelemetryDataOnlyInfo';

export const ColumnConfigs = {
  FilterProfileName: {
    MinLength: 3,
    MaxLength: 50,
  },
  TankProfileName: {
    MinLength: 3,
    MaxLength: 100,
  },
};
